import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AccountType } from '../../enums/account-type';
import { AccountService } from '../../api/services/account.service';
import { HttpHeaders } from '@angular/common/http';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { AccountModel } from '../../api/models/account-model';
import { UserService } from '../../api/services/user.service';
import {
  AccountSelectionRefreshResult,
  AccountStateService,
} from '../../services/account-state.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  standalone: false,
})
export class RegisterComponent {
  /**
   * Form used to start the registration process
   */
  registerForm = new FormGroup({
    email: new FormControl('', [Validators.email, Validators.required]),
    mobile: new FormControl(''),
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl(''),
    agreesToTermsAndConditions: new FormControl<boolean>(
      false,
      Validators.requiredTrue,
    ),
    subscribeToEmailNotifications: new FormControl<boolean>(false),
    accountType: new FormControl<AccountType>(AccountType.Personal),
    password: new FormControl(''),
    confirmPassword: new FormControl(''),
    organisationDetails: new FormGroup({
      organisationName: new FormControl(''),
      contactEmailAddress: new FormControl(''),
      contactMobileNumber: new FormControl(''),
    }),
    personalAccountName: new FormControl<string>(''),
  });

  /**
   * The current registration step
   */
  currentStep = 0;

  /**
   * The total number of steps
   */
  totalSteps = 3;

  /**
   * The password strength score
   * 0 = Weak
   * 1 = Medium
   * 2 = Strong
   */
  passwordStrengthScore = 0;

  /**
   * Whether the person registering the account is an existing user, meaning that the user setup can be skipped
   */
  isExistingUser = false;

  /**
   * Whether the account create API request is currently being executed
   */
  accountCreating = false;
  protected readonly AccountType = AccountType;

  constructor(
    private accountService: AccountService,
    private toastService: ToastService,
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private accountStateService: AccountStateService,
  ) {}

  ngOnInit() {
    // Set form field values from login page state
    this.registerForm.controls.email.patchValue(window.history.state.email);
    this.registerForm.controls.firstName.patchValue(
      window.history.state.firstName,
    );
    // If existing user state is set, update property
    this.isExistingUser = window.history.state.isExistingUser ?? false;
  }

  createAccount() {
    this.accountCreating = true;
    if (this.isExistingUser) {
      this.createExistingUserAccount();
    } else {
      this.createNewUserAccount();
    }
  }

  /**
   * Creates an account along with a user
   */
  createNewUserAccount() {
    this.accountService
      .newUserAccountPost(
        {
          body: {
            firstName: this.registerForm.controls.firstName.value!,
            lastName: this.registerForm.controls.lastName.value!,
            email: this.registerForm.controls.email.value!,
            password: this.registerForm.controls.password.value!,
            mobile: this.registerForm.controls.mobile.value ?? undefined,
            accountType: this.registerForm.controls.accountType.value!,
            organisationDetails:
              this.registerForm.controls.accountType.value ===
              AccountType.Organisation
                ? {
                    name: this.registerForm.controls.organisationDetails
                      .controls.organisationName.value!,
                    emailAddress:
                      this.registerForm.controls.organisationDetails.controls
                        .contactEmailAddress.value!,
                    mobile:
                      this.registerForm.controls.organisationDetails.controls
                        .contactMobileNumber.value ?? undefined,
                  }
                : undefined,
          },
        },
        new HttpHeaders(),
      )
      .subscribe({
        next: () => {
          // Authenticate user and navigate to home page
          this.authService
            .login(
              this.registerForm.controls.email.value!,
              this.registerForm.controls.password.value!,
            )
            .subscribe({
              next: async (res) => {
                this.toastService.success('Successfully Created Account');
                this.authService.setTokens(res.body);
                let accountSelection: AccountSelectionRefreshResult;
                try {
                  accountSelection =
                    await this.accountStateService.refreshAccountsAndReconcileSelected(
                      res.body.selectedAccountGuid,
                    );
                } catch (error) {
                  console.error(error);
                  this.authService.clearAuthState();
                  this.toastService.error('Could not load your accounts');
                  return;
                }

                if (accountSelection.status === 'no-active-accounts') {
                  this.authService.clearAuthState();
                  this.toastService.error(
                    'Your account is inactive. Please contact support or your account administrator.',
                  );
                  return;
                }

                void this.router.navigate(['admin/home']);
              },
              error: () => {
                this.toastService.error('Could Not Log In');
              },
            });
        },
        error: (err) => {
          this.toastService.error(`Could not create account - ${err.body}`);
        },
      });
  }

  /**
   * Creates an account for an existing user
   */
  createExistingUserAccount() {
    this.accountService
      .existingUserAccountPost(
        {
          userGuid: this.authService.getUserGuid()!,
          body: {
            accountType: this.registerForm.controls.accountType.value!,
            personalWorkspaceName:
              this.registerForm.controls.personalAccountName.value!,
            organisationDetails:
              this.registerForm.controls.accountType.value ===
              AccountType.Organisation
                ? {
                    name: this.registerForm.controls.organisationDetails
                      .controls.organisationName.value!,
                    emailAddress:
                      this.registerForm.controls.organisationDetails.controls
                        .contactEmailAddress.value!,
                    mobile:
                      this.registerForm.controls.organisationDetails.controls
                        .contactMobileNumber.value ?? undefined,
                  }
                : undefined,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .subscribe({
        next: (account: AccountModel) => {
          // Swap user to newly created account, and navigate to home page
          this.userService
            .userSwapAccount$Response(
              {
                userGuid: this.authService.getUserGuid()!,
                body: {
                  accountGuid: account.guid,
                },
              },
              this.authService.getAuthorisationHeaders(),
            )
            .subscribe({
              next: async () => {
                this.toastService.success('New Account Created');
                this.authService.setActiveAccountGuid(account.guid);
                try {
                  await this.accountStateService.refreshAccountsAndReconcileSelected(
                    account.guid,
                  );
                } catch (error) {
                  console.error(error);
                  this.toastService.error('Could not refresh your accounts');
                  return;
                }
                void this.router.navigate(['admin/home']);
              },
              error: () => {
                this.toastService.error('Could Not Switch Account');
              },
            });
        },
        error: (err) => {
          this.toastService.error(`Could not create account - ${err.body}`);
        },
      });
  }

  nextStep() {
    if (this.currentStep === 0) {
      // For existing users, go straight to step 3 (org / account name setup) after selecting account type
      if (this.isExistingUser) {
        this.currentStep = 3;
      } else {
        this.currentStep = this.currentStep + 1;
      }
    } else if (this.currentStep == 1) {
      // Validate email address first
      this.accountService
        .emailAddressAvailablePost$Response(
          { emailAddress: this.registerForm.controls.email.value! },
          this.authService.getAuthorisationHeaders(),
        )
        .subscribe({
          next: () => (this.currentStep = this.currentStep + 1),
          error: () => this.toastService.error('Email address already in use'),
        });
    } else if (
      this.currentStep === 2 &&
      this.registerForm.controls.accountType.value === AccountType.Personal
    ) {
      // If at step 2, and creating personal account, skip the org details section and create account
      this.createAccount();
    } else if (this.currentStep === 3) {
      // If at step 2, we are finished, so create account
      this.createAccount();
    } else {
      // Move to nextstep
      this.currentStep = this.currentStep + 1;
    }
  }

  previousStep() {
    if (this.currentStep === 0) {
      if (this.isExistingUser) {
        void this.router.navigate(['admin', 'home']);
      } else {
        void this.router.navigate(['/']);
      }
    }
    if (this.isExistingUser) {
      this.currentStep = 0;
      return;
    }
    this.currentStep = this.currentStep - 1;
  }

  updateAccountType(event: Event) {
    this.registerForm.controls.accountType.patchValue(
      (event.target as HTMLInputElement).value as AccountType,
    );
  }

  updatePasswordScore() {
    const password = this.registerForm.controls.password.value;

    if (!password) {
      this.passwordStrengthScore = 0;
      return;
    }

    // Password length determines strength. Less than 8 chars is weak, 8-11 is medium, and 12+ is strong
    if (password.length < 8) {
      this.passwordStrengthScore = 0;
    } else if (password.length < 12) {
      this.passwordStrengthScore = 1;
    } else {
      this.passwordStrengthScore = 2;
    }
  }

  /**
   * Determines whether user can move to next step based on current form state
   */
  canMoveToNextStep() {
    if (this.currentStep === 1) {
      return !!(
        this.registerForm.controls.firstName.value &&
        this.registerForm.controls.lastName.value &&
        this.registerForm.controls.email.value &&
        this.registerForm.controls.agreesToTermsAndConditions.value === true
      );
    } else if (this.currentStep === 2) {
      return this.passwordsMatch();
    } else if (this.currentStep === 3) {
      if (
        !this.isExistingUser ||
        this.registerForm.controls.accountType.value ===
          AccountType.Organisation
      ) {
        return !!(
          this.registerForm.controls.organisationDetails.controls
            .organisationName.value &&
          this.registerForm.controls.organisationDetails.controls
            .contactEmailAddress.value
        );
      } else {
        return !!this.registerForm.controls.personalAccountName.value;
      }
    }

    return true;
  }

  passwordsMatch() {
    return !!(
      this.registerForm.controls.password.value &&
      this.registerForm.controls.confirmPassword.value &&
      this.registerForm.controls.password.value ===
        this.registerForm.controls.confirmPassword.value
    );
  }

  getProgressBarWidth(): string {
    const progress =
      this.registerForm.controls.accountType.value === AccountType.Personal &&
      !this.isExistingUser
        ? this.currentStep / this.totalSteps
        : this.currentStep / (this.totalSteps + 1);

    return `${progress * 100}%`;
  }
}
